/**
 * Exact dice odds for bots: a small dynamic program over (locked held dice, rolls left).
 *
 * Each roll the player keeps some dice, rerolls the rest, optionally changes dice with their
 * card's power, then either attacks or holds a subset for the next roll. The payoff of an attack
 * is the summed value of every target it destroys: natural kills (`canKill`) plus the card's
 * free kills (`freeKills`), which take the most valuable targets the dice don't already kill.
 * Holding earns `rho` times the best payoff of the next roll, where `rho` is the chance of
 * surviving to it, so "take the kill now or gamble for more" is just `max(now, rho * later)`.
 *
 * With one target of value 1 and `rho = 1` this is P(kill by roll N) under optimal holds;
 * `docs/design/bot-odds-tables.md` lists those numbers. Held dice are locked (powers only touch
 * dice rolled this turn) unless the pool is five of a kind, which sets every die to anything.
 */
import { DICE_COUNT, DIE_FACES, MAX_SAVED_DICE } from './constants.js';
import { KILL_ODDS_MAX_ROLLS, KILL_ODDS_TABLE } from './killOddsTable.js';
import { affectsKills, canKill, freeKills } from './rules.js';
const key = (dice) => [...dice].sort((a, b) => a - b).join('');
/** The multiset of dice as a number (each face's count in base 6): `key` without sorting or strings, for the hot paths. */
function code(dice) {
    let c = 0;
    for (const d of dice)
        c += POW6[d - 1];
    return c;
}
const POW6 = [1, 6, 36, 216, 1296, 7776];
/** Above every `code` (counts are at most 5). */
const CODES = 6 ** DIE_FACES;
/** All sorted tuples of `n` faces. */
function multisets(n, from = 1) {
    if (n === 0)
        return [[]];
    const out = [];
    for (let f = from; f <= DIE_FACES; f++)
        for (const rest of multisets(n - 1, f))
            out.push([f, ...rest]);
    return out;
}
const FACTORIAL = [1, 1, 2, 6, 24, 120];
/** Probability of rolling this multiset with n dice. */
function probability(dice) {
    const counts = new Array(DIE_FACES + 1).fill(0);
    for (const d of dice)
        counts[d] = counts[d] + 1;
    let ways = FACTORIAL[dice.length];
    for (const c of counts)
        ways /= FACTORIAL[c];
    return ways / DIE_FACES ** dice.length;
}
const MULTISETS = Array.from({ length: DICE_COUNT + 1 }, (_, n) => multisets(n));
const ROLLS = MULTISETS.map((sets) => sets.map((m) => ({ dice: m, p: probability(m) })));
/**
 * Distinct sub-multisets of `dice`, as index lists (one representative per distinct value set),
 * of at most `max` dice.
 */
function holdSets(dice, required = new Set(), max = DICE_COUNT) {
    // Cached by the dice in order (base 7), the required indices (a bit mask) and `max`: the answer is index lists.
    let need = 0;
    for (const r of required)
        need |= 1 << r;
    let ordered = 0;
    for (const d of dice)
        ordered = ordered * 7 + d;
    const ck = (ordered * 32 + need) * 8 + max;
    const hit = HOLD_SETS.get(ck);
    if (hit)
        return hit;
    const seen = new Map();
    for (let mask = 0; mask < 1 << dice.length; mask++) {
        if ((mask & need) !== need)
            continue;
        const idx = [];
        let c = 0;
        for (let i = 0; i < dice.length; i++)
            if ((mask >> i) & 1) {
                idx.push(i);
                c += POW6[dice[i] - 1];
            }
        if (idx.length > max)
            continue;
        if (!seen.has(c))
            seen.set(c, idx);
    }
    const out = [...seen.values()];
    HOLD_SETS.set(ck, out);
    return out;
}
const HOLD_SETS = new Map();
export class OddsEngine {
    query;
    /** By `t * CODES + code(held)`. */
    memo = new Map();
    /** By free-kill weight, then `code(dice)`. */
    payoffs = new Map();
    bestConfigs = new Map();
    wild;
    up;
    down;
    constructor(query) {
        this.query = query;
        const w = /^w([1-6])$/.exec(query.power ?? '');
        this.wild = w ? Number(w[1]) : null;
        this.up = query.power === 'xxx' || query.power === 'geq25';
        this.down = query.power === 'xxx' || query.power === 'leq10';
    }
    /** Free-kill weight for rolls still to come, and for an attack on the dice already rolled. */
    get aheadWeight() {
        return this.query.selfWeight ?? 1;
    }
    get nowWeight() {
        return Math.max(1, this.aheadWeight);
    }
    /** Holding into a roll with `t` rolls left: the chance of surviving to it. */
    rhoInto(t) {
        return t === this.query.freeAt ? 1 : this.query.rho;
    }
    /** Payoff and killed targets if these five dice are attacked with (free kills weighted by `selfWeight`). */
    payoff(dice, selfWeight = 1) {
        let byDice = this.payoffs.get(selfWeight);
        if (!byDice)
            this.payoffs.set(selfWeight, (byDice = new Map()));
        const k = code(dice);
        const hit = byDice.get(k);
        if (hit)
            return hit;
        const kills = [];
        const rest = [];
        for (const t of this.query.targets)
            (canKill(dice, t.killer) ? kills : rest).push(t);
        const free = freeKills(this.query.power, dice);
        const extra = free > 0 ? [...rest].sort((a, b) => b.value - a.value).slice(0, free) : [];
        const sum = (ts) => ts.reduce((s, t) => s + t.value, 0);
        const out = { value: sum(kills) + selfWeight * sum(extra), kills: [...kills, ...extra].map((t) => t.id) };
        byDice.set(k, out);
        return out;
    }
    /** Best attack available when every die can be set freely (five of a kind). */
    best(selfWeight) {
        let best = this.bestConfigs.get(selfWeight);
        if (!best) {
            best = { dice: [1, 1, 1, 1, 1], value: -1 };
            for (const m of MULTISETS[DICE_COUNT]) {
                const value = this.payoff(m, selfWeight).value;
                if (value > best.value)
                    best = { dice: m, value };
            }
            this.bestConfigs.set(selfWeight, best);
        }
        return best;
    }
    /** Final dice choices after power use: each is the full five dice plus the mods that produce it. */
    options(held, fresh) {
        const pool = [...held, ...fresh];
        const out = [{ dice: pool, mods: [] }];
        const h = held.length;
        if (this.wild !== null) {
            const at = fresh.flatMap((v, i) => (v === this.wild ? [h + i] : []));
            if (at.length > 0) {
                for (const m of MULTISETS[at.length]) {
                    const dice = [...pool];
                    const mods = [];
                    at.forEach((idx, j) => {
                        dice[idx] = m[j];
                        if (m[j] !== pool[idx])
                            mods.push({ index: idx, value: m[j] });
                    });
                    if (mods.length > 0)
                        out.push({ dice, mods });
                }
            }
        }
        else if (this.up || this.down) {
            for (let i = 0; i < fresh.length; i++) {
                if (i > 0 && fresh[i] === fresh[i - 1])
                    continue;
                const v = fresh[i];
                const idx = h + i;
                for (const nv of [v + 1, v - 1]) {
                    if (nv < 1 || nv > DIE_FACES || (nv > v ? !this.up : !this.down))
                        continue;
                    const dice = [...pool];
                    dice[idx] = nv;
                    out.push({ dice, mods: [{ index: idx, value: nv }] });
                }
            }
        }
        return out;
    }
    /** Expected payoff with `t` rolls left (including the next one) and these dice locked in hand. */
    value(t, held) {
        if (t <= 0)
            return 0;
        const k = t * CODES + code(held);
        const hit = this.memo.get(k);
        if (hit !== undefined)
            return hit;
        let total = 0;
        for (const { dice: fresh, p } of ROLLS[DICE_COUNT - held.length]) {
            const pool = [...held, ...fresh];
            if (pool.length === DICE_COUNT && new Set(pool).size === 1) {
                total += p * this.best(this.aheadWeight).value;
                continue;
            }
            let best = 0;
            for (const o of this.options(held, fresh)) {
                best = Math.max(best, this.payoff(o.dice, this.aheadWeight).value);
                if (t > 1) {
                    const modded = new Set(o.mods.map((m) => m.index));
                    for (const idx of holdSets(o.dice, modded)) {
                        best = Math.max(best, this.rhoInto(t - 1) *
                            this.value(t - 1, idx.map((i) => o.dice[i])));
                    }
                }
            }
            total += p * best;
        }
        this.memo.set(k, total);
        return total;
    }
    /**
     * The best move for dice already rolled this turn. `pool` is the whole pool: the first
     * `heldCount` are locked from earlier turns. `rollsAfter` is how many rolls remain after this one.
     */
    decide(pool, heldCount, rollsAfter) {
        const held = pool.slice(0, heldCount);
        const fresh = pool.slice(heldCount);
        const all = pool.map((_, i) => i);
        if (pool.length === DICE_COUNT && new Set(pool).size === 1) {
            const b = this.best(this.nowWeight);
            const mods = b.dice.flatMap((value, index) => (value !== pool[index] ? [{ index, value }] : []));
            return { action: 'attack', mods, keep: all, kills: this.payoff(b.dice, this.nowWeight).kills, value: b.value };
        }
        let best = { action: 'end', mods: [], keep: [], kills: [], value: 0 };
        let bestKey = '';
        for (const o of this.options(held, [...fresh].sort((a, b) => a - b))) {
            // `options` works on sorted fresh dice; map mods back onto the real pool indices by value.
            const mods = this.remap(pool, heldCount, o.mods, fresh);
            const dice = [...pool];
            for (const m of mods)
                dice[m.index] = m.value;
            const atk = this.payoff(dice, this.nowWeight);
            if (atk.value > best.value)
                best = { action: 'attack', mods, keep: all, kills: atk.kills, value: atk.value };
            if (rollsAfter > 0) {
                const modded = new Set(mods.map((m) => m.index));
                // Ending a turn holding all five ends the round. (Lookahead may still hold five: that
                // stands in for a power-made five of a kind going wild, which `value` doesn't model.)
                for (const idx of holdSets(dice, modded, MAX_SAVED_DICE)) {
                    const v = this.rhoInto(rollsAfter) *
                        this.value(rollsAfter, idx.map((i) => dice[i]));
                    const tie = key(idx.map((i) => dice[i]));
                    // Ties (to float error) go to the attack in hand: holding only pays when it can add something.
                    if (v > best.value + 1e-9 || (Math.abs(v - best.value) <= 1e-9 && best.action === 'end' && tie > bestKey)) {
                        best = { action: 'end', mods, keep: idx, kills: [], value: v };
                        bestKey = tie;
                    }
                }
            }
        }
        return best;
    }
    /** `options` was computed on sorted fresh dice; translate its mods onto the unsorted pool. */
    remap(pool, heldCount, mods, fresh) {
        if (mods.length === 0)
            return mods;
        const sorted = [...fresh].sort((a, b) => a - b);
        const used = new Set();
        return mods.map((m) => {
            const original = sorted[m.index - heldCount];
            const at = pool.findIndex((v, i) => i >= heldCount && v === original && !used.has(i));
            used.add(at);
            return { index: at, value: m.value };
        });
    }
}
/** P(kill one target by roll `rolls`) with optimal holds: the numbers in the odds tables. */
export function killProbability(killer, power, rolls) {
    return new OddsEngine({ targets: [{ id: 0, killer, value: 1 }], power, rho: 1 }).value(rolls, []);
}
const engines = new Map();
/**
 * P(kill one target within `rolls` rolls) starting from `held` locked dice, under optimal holds.
 * From an empty hand the answer comes from the precomputed `KILL_ODDS_TABLE`; otherwise engines
 * (and their memo tables) are cached per killer and power, so repeated queries are cheap.
 * Powers that don't affect kills (Two Pair, Full House, Small Straight) count as no power.
 */
export function killOdds(killer, power, rolls, held = []) {
    if (rolls <= 0)
        return 0;
    const p = affectsKills(power) ? power : '';
    const k = `${killer}|${p}`;
    const cached = held.length === 0 && rolls <= KILL_ODDS_MAX_ROLLS ? KILL_ODDS_TABLE[k]?.[rolls - 1] : undefined;
    if (cached !== undefined)
        return cached;
    let e = engines.get(k);
    if (!e)
        engines.set(k, (e = new OddsEngine({ targets: [{ id: 0, killer, value: 1 }], power: p || null, rho: 1 })));
    return e.value(rolls, held);
}
//# sourceMappingURL=odds.js.map